import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BoxIcons, HomeOutlined, IconTypes } from "us.icons";
import { $Divider, $Tooltip, $Button, $Skeleton } from "us.common/components";
import packageJson from "../../../../../../../package.json";
import { IOperation, INavigationData } from "../../interface";
import { getRouteUrl } from "us.helper";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "us.helper/types";
import unicornLogo from "us.icons/svg/unicorn_logo.svg";

interface IMegaMenuCommonViewProps {
  quickLinks: any[];
  unpin: (operation: IOperation) => void;
  onNavigate: (url: string, state?: INavigationData) => void;
}

const CommonView: React.FC<IMegaMenuCommonViewProps> = ({
  quickLinks,
  unpin,
  onNavigate,
}) => {
  const { t } = useTranslation(["US.COLLECTION.COMMON"], {
    useSuspense: true,
  });
  const { state: locationState } = useLocation();
  const state = locationState as INavigationData;
  const [selectedQuickLink, setSelectedQuickLink] =
    useState<IOperation | null>();

  const { isSaving, isLoading } = useSelector(
    (state: RootState) => state.UserInformation.quickLinks
  );

  // handle quick links navigation
  const handleQuickLinks = (operation: IOperation) => {
    const { route, module } = operation;

    if (window.APP_MODULE === module && route) {
      onNavigate(route, state);
    } else {
      getRouteUrl.moduleRoute(module, route);
    }
  };

  // hadle static menu item in the left list of the menu
  const handleStaticMenuItemNavigation = (
    url: string,
    moduleName: string,
    state?: INavigationData
  ) => {
    if (window.APP_MODULE === moduleName) {
      onNavigate(url, state);
    } else {
      getRouteUrl.moduleRoute(moduleName, url);
    }
  };

  return (
    <div className="mega-menu-wrapper-fixed-left d-flex flex-column justify-content-between">
      <div className="mt-3 quick-link-wrap">
        <a
          href={window._ENV.REACT_APP_HOME_URL}
          tabIndex={-1}
          className="d-flex align-items-center justify-content-start quick-link-item"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="mg-menu-icon-outer">
            <HomeOutlined />
          </span>
          <span className="link-label" tabIndex={-1}>
            {t("US.COMMON:COMMON.HOME")}
          </span>
        </a>
        <div
          className="d-flex align-items-center justify-content-start quick-link-item"
          tabIndex={-1}
          onClick={() => getRouteUrl.moduleRoute("collection", "/follow-up")}
        >
          <span className="mg-menu-icon-outer">
            <BoxIcons type={IconTypes.BOX_ICON} name="add-followup" />
          </span>
          <span className="link-label" tabIndex={-1}>
            {t("US.COMMON:COMMON.FOLLOW_UP")}
          </span>
        </div>
        <div
          className="d-flex align-items-center justify-content-start quick-link-item"
          tabIndex={-1}
          onClick={() => {
            handleStaticMenuItemNavigation(
              "/bureau/manage-creditor-groups",
              "collection",
              {
                ...state,
                currentTab: "bureau",
              }
            );
          }}
        >
          <span className="mg-menu-icon-outer">
            <BoxIcons type={IconTypes.BOX_ICON} name="creditor" />
          </span>
          <span className="link-label" tabIndex={-1}>
            {t("US.COLLECTION.COMMON:COMMON.CREATE_NEW_CREDITOR")}
          </span>
        </div>
        {quickLinks?.length > 0 && (
          <>
            <$Divider className="mb-2" />
            <div className="text-muted mb-1">
              <span>{t("US.COMMON:COMMON.QUICK_LINKS")}</span>
            </div>
          </>
        )}
        <div className="d-flex flex-column">
          <$Skeleton loading={isLoading} paragraph={{ rows: 2 }}>
            {quickLinks?.map((operation: IOperation) => {
              const { id, displayName } = operation;
              return (
                <div
                  className="feature-link-item d-flex align-items-center justify-content-between quick-link-item"
                  onMouseEnter={() => {
                    if (selectedQuickLink?.id !== id) {
                      setSelectedQuickLink(operation);
                    }
                  }}
                  onMouseLeave={() => setSelectedQuickLink(null)}
                  onClick={() => handleQuickLinks(operation)}
                  key={id}
                >
                  <span className="link-label" key={id}>
                    {displayName}
                  </span>
                  <span>
                    {selectedQuickLink?.id === id && (
                      <$Tooltip
                        placement="top"
                        title={t(
                          "US.COLLECTION.COMMON:COMMON.UNPIN_FROM_QUICK_LINKS"
                        )}
                      >
                        <$Button
                          onClick={(e: any) => {
                            e.stopPropagation();
                            unpin(operation);
                          }}
                          size="small"
                          type="link"
                          disabled={isSaving}
                        >
                          <BoxIcons type={IconTypes.BOX_ICON} name="unpin" />
                        </$Button>
                      </$Tooltip>
                    )}
                  </span>
                </div>
              );
            })}
          </$Skeleton>
        </div>
      </div>
      <div className="menu-footer">
        <$Divider className="mb-2" />
        <div className="text-muted d-flex align-items-center font-sm mb-2">
          {t("US.COMMON:COMMON.MODULE_VERSION")}: v{packageJson.version}
        </div>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <svg viewBox="0 0 95 25" width="95" height="25">
              <g>
                <g>
                  <path
                    fill="#5FBA4E"
                    d="M69,7.1v11.9h11.9V7.1H69z M75.2,17.1c-2.8,0-4.1-2-4.1-4.1c0-2.1,1.3-4.2,4.1-4.2c2.7,0,4.1,2.1,4.1,4.2
		C79.2,15.1,78,17.1,75.2,17.1z"
                  />
                  <path
                    fill="#5FBA4E"
                    d="M75.2,10.2c-1.9,0-2.6,1.5-2.6,2.8c0,1.3,0.7,2.7,2.6,2.7c1.9,0,2.6-1.4,2.6-2.7C77.8,11.7,77,10.2,75.2,10.2
		z"
                  />
                </g>
                <path
                  fill="#9FCB3A"
                  d="M83,7.1v11.9h11.9V7.1H83z M92.6,17h-1.7l-2-3l-2,3h-1.7v0l2.7-4l-2.7-3.8V9.1h1.6l2,3l2-3h1.6v0.1L89.9,13
	L92.6,17L92.6,17z"
                />
                <g>
                  <path
                    fill="#129BD2"
                    d="M55.2,7.1v11.8H67V7.1H55.2z M61.7,17c-1.3,0-2.5,0-3.8,0V9.1c1.2,0,2.5,0,3.8,0c1.6,0,2.7,0.7,2.7,2.3
		c0,0.6-0.3,1.2-1,1.5c0.9,0.3,1.3,1.3,1.3,1.8C64.6,16.4,63.3,17,61.7,17z"
                  />
                  <path
                    fill="#129BD2"
                    d="M62.9,11.4c0-0.4-0.3-1-1.2-1h-2.3v1.8h2.3C62.6,12.2,62.9,11.8,62.9,11.4z"
                  />
                  <path
                    fill="#129BD2"
                    d="M61.7,13.5h-2.3v2h2.3c0.6,0,1.5-0.2,1.5-0.9C63.2,13.9,62.3,13.5,61.7,13.5z"
                  />
                </g>
                <path
                  fill="#435DA9"
                  d="M6.6,17.9v-1.5c-0.6,1.2-1.8,1.6-3,1.7c-2.3,0-3.8-1.4-3.8-3.7V9.5h0.8v4.9c0,1.9,1.2,2.9,3,2.9
	c1.7,0,3-1.3,3-3.1V9.5h0.8v8.5H6.6z"
                />
                <path
                  fill="#435DA9"
                  d="M10.1,9.5V11c0.7-1.2,1.8-1.6,3-1.7c2.3,0,3.8,1.4,3.8,3.7v4.9H16v-4.9c0-1.9-1.2-2.9-3-2.9
	c-1.7,0-3,1.3-3,3.1v4.7H9.2V9.5H10.1z"
                />
                <path
                  fill="#435DA9"
                  d="M19.8,6.7c0,0.9-1.4,0.9-1.4,0C18.4,5.8,19.8,5.8,19.8,6.7z M18.6,9.5v8.5h0.8V9.5H18.6z"
                />
                <path
                  fill="#435DA9"
                  d="M28.4,16.8c-0.9,0.9-2,1.3-3.1,1.3c-2.4,0-4.3-1.6-4.3-4.3c0-2.7,1.9-4.3,4.3-4.3c1.1,0,2.2,0.4,3.1,1.3
	l-0.5,0.5c-0.7-0.7-1.6-1-2.5-1c-1.9,0-3.5,1.2-3.5,3.5c0,2.3,1.6,3.5,3.5,3.5c0.9,0,1.8-0.4,2.5-1L28.4,16.8z"
                />
                <path
                  fill="#435DA9"
                  d="M29.4,13.7c0-2.7,1.9-4.4,4.3-4.4c2.4,0,4.3,1.7,4.3,4.4c0,2.7-1.9,4.3-4.3,4.3C31.3,18.1,29.4,16.4,29.4,13.7
	z M37.2,13.7c0-2.2-1.6-3.6-3.5-3.6c-1.9,0-3.5,1.4-3.5,3.6c0,2.2,1.6,3.5,3.5,3.5C35.6,17.2,37.2,15.9,37.2,13.7z"
                />
                <path
                  fill="#435DA9"
                  d="M40.3,9.5l0,1.5c0.5-1.1,1.7-1.6,2.8-1.6c0.6,0,1.2,0.2,1.8,0.5l-0.4,0.7c-0.4-0.3-0.9-0.4-1.4-0.4
	c-1.6,0-2.8,1.3-2.8,2.8v4.9h-0.8V9.5H40.3z"
                />
                <path
                  fill="#435DA9"
                  d="M46.6,9.5V11c0.6-1.2,1.8-1.6,3-1.7c2.3,0,3.8,1.4,3.8,3.7v4.9h-0.8v-4.9c0-1.9-1.2-2.9-3-2.9
	c-1.7,0-3,1.3-3,3.1v4.7h-0.8V9.5H46.6z"
                />
              </g>
            </svg>
          </div>
          <div>
            <a
              className="font-sm"
              target="_blank"
              href="https://unicorn-solutions.com/"
              tabIndex={-1}
              rel="noopener noreferrer"
            >
              Unicorn Solutions AS&copy;
            </a>
          </div>
        </div>
        <div className="font-sm text-muted d-flex justify-content-between align-items-center foot-link">
          <div>
            <a href="https://unicorn-solutions.com/about-us.html" tabIndex={-1}>
              About
            </a>{" "}
            <span className="mx-1">|</span>
            <a href="#" tabIndex={-1}>
              Terms of Use
            </a>{" "}
            <span className="mx-1">|</span>
            <a href="#" tabIndex={-1}>
              Privacy Policy
            </a>
          </div>
          <div>
            <img alt="unicorn-logo" src={unicornLogo} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonView;
